import { useEffect, useMemo, useRef, useState } from "react";

export const isValidSponsorImage = (src = "") => {
  if (!src || typeof src !== "string") return false;
    const value = src.trim();
    if (!value) return false;
    return /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(value);
};

export const getThemeState = () => {
  if (typeof document === "undefined") return { resolved: false, isDark: false };
  const themeAttr = document.documentElement.getAttribute("data-theme");
  if (themeAttr === "dark") return { resolved: true, isDark: true };
  if (themeAttr === "light") return { resolved: true, isDark: false };
  
  // CRUCIAL: Si no hay atributo, preguntar al sistema. 
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return { resolved: false, isDark: prefersDark };
};

const isMobile = () => typeof window !== "undefined" && window.innerWidth <= 1024;

export const getSkeletonColor = (isDark, mobile) => {
  if (isDark && mobile) return "transparent";
  return "#ffffff"; 
};

const pickImageSrc = ({ linkImageDesktop, linkImageMobile, linkImageDark }) => {
  const { resolved, isDark } = getThemeState();
  const mobile = isMobile();

  if (!mobile) {
    return isValidSponsorImage(linkImageDesktop) ? linkImageDesktop : null;
  }

  // Mobile: sin usar nunca linkImageDesktop; solo linkImageDark (oscuro) o linkImageMobile (claro)
  if (isValidSponsorImage(linkImageDark) && !resolved) return null;
  if (isDark) {
    return isValidSponsorImage(linkImageDark) ? linkImageDark : null;
  }
  return isValidSponsorImage(linkImageMobile) ? linkImageMobile : null;
};

export const useSeparatorBannerImage = ({
  linkImageDesktop,
  linkImageMobile,
  linkImageDark,
}) => {

  const MIN_SKELETON_MS = 600; 
  const preloadRequestRef = useRef(0);
  const revealTimeoutRef = useRef(null);

  const theme = useMemo(() => getThemeState(), []);
  const mobileFlag = useMemo(() => isMobile(), []);

  const [imageSrc, setImageSrc] = useState(null);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [skeletonColor, setSkeletonColor] = useState(() => 
    getSkeletonColor(theme.isDark, mobileFlag)
  );

  useEffect(() => {
    const hasAnySrc =
      isValidSponsorImage(linkImageDesktop) ||
      isValidSponsorImage(linkImageMobile) ||
      isValidSponsorImage(linkImageDark);

    if (!hasAnySrc) {
      setShowSkeleton(false);
      setImageSrc(null);
      return;
    }

    const loadImage = () => {
      const currentTheme = getThemeState();
      const currentMobile = isMobile();

      setSkeletonColor(getSkeletonColor(currentTheme.isDark, currentMobile));

      const next = pickImageSrc({ linkImageDesktop, linkImageMobile, linkImageDark });

      if (next === null) {
        if (isMobile() && isValidSponsorImage(linkImageDark) && !currentTheme.resolved) {
          setShowSkeleton(true);
          return;
        }
        setShowSkeleton(false);
        setImageSrc(null);
        return;
      }

      preloadRequestRef.current += 1;
      const requestId = preloadRequestRef.current;
      
      if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
      
      setShowSkeleton(true);
      const loadStartedAt = Date.now();
      const preload = new window.Image();

      const revealImage = () => {
        if (requestId !== preloadRequestRef.current) return;
        setImageSrc(next);
        setShowSkeleton(false);
      };
      preload.onload = () => {
        const elapsed = Date.now() - loadStartedAt;
        const waitTime = Math.max(0, MIN_SKELETON_MS - elapsed);
        revealTimeoutRef.current = window.setTimeout(revealImage, waitTime);
      };
      preload.onerror = revealImage;
      preload.src = next;
    };

    loadImage();

    const observer = new MutationObserver(() => loadImage());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(loadImage, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      preloadRequestRef.current += 1;
      if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [linkImageDesktop, linkImageMobile, linkImageDark]);

  return { imageSrc, showSkeleton, skeletonColor };
};